
export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    {
      id: 'data-analytics',
      title: 'Data and Analytics',
      description: 'Transform raw data into actionable insights with our advanced analytics platform. Harness the power of real-time data processing and visualization.',
      features: ['Real-time Processing', 'Advanced Visualization', 'Predictive Analytics', 'Custom Dashboards'],
      bgImage: 'https://readdy.ai/api/search-image?query=futuristic%20data%20analytics%20dashboard%20with%20holographic%20charts%20floating%20in%20a%20high-tech%20digital%20environment%2C%20neon%20blue%20and%20purple%20lights%2C%20abstract%20geometric%20patterns%2C%20glass%20surfaces%20reflecting%20data%20streams%2C%20minimalist%20modern%20workspace%20with%20ambient%20lighting&width=1920&height=2400&seq=data-analytics-bg&orientation=portrait',
      icon: 'ri-bar-chart-line'
    },
    {
      id: 'business-intelligence',
      title: 'Business Intelligence Solutions',
      description: 'Empower decision-makers with comprehensive BI tools that provide deep insights into business performance and market trends.',
      features: ['Executive Dashboards', 'KPI Monitoring', 'Report Automation', 'Data Mining'],
      bgImage: 'https://readdy.ai/api/search-image?query=sophisticated%20business%20intelligence%20command%20center%20with%20multiple%20holographic%20displays%20showing%20financial%20charts%20and%20metrics%2C%20corporate%20boardroom%20setting%2C%20blue%20and%20silver%20color%20scheme%2C%20professional%20lighting%2C%20glass%20conference%20table%20reflecting%20data%20projections&width=1920&height=2400&seq=bi-solutions-bg&orientation=portrait',
      icon: 'ri-lightbulb-line'
    },
    {
      id: 'big-data',
      title: 'Big Data Solutions',
      description: 'Handle massive datasets with our scalable big data infrastructure. Process petabytes of information with lightning-fast performance.',
      features: ['Hadoop Ecosystem', 'Spark Processing', 'Cloud Storage', 'Data Lakes'],
      bgImage: 'https://readdy.ai/api/search-image?query=massive%20data%20center%20with%20servers%20extending%20into%20infinity%2C%20flowing%20streams%20of%20digital%20data%20represented%20as%20glowing%20particles%2C%20dark%20environment%20with%20blue%20and%20green%20accent%20lighting%2C%20futuristic%20architecture%20with%20geometric%20patterns&width=1920&height=2400&seq=big-data-bg&orientation=portrait',
      icon: 'ri-database-line'
    },
    {
      id: 'database-management',
      title: 'Database Management',
      description: 'Optimize your database performance with our comprehensive management solutions. Ensure data integrity and maximum uptime.',
      features: ['Performance Tuning', 'Backup & Recovery', 'Security Management', 'Migration Tools'],
      bgImage: 'https://readdy.ai/api/search-image?query=elegant%20database%20architecture%20visualization%20with%20interconnected%20nodes%20and%20data%20pathways%2C%20clean%20white%20and%20blue%20environment%2C%20transparent%20cylindrical%20database%20structures%2C%20soft%20ambient%20lighting%2C%20minimalist%20high-tech%20aesthetic&width=1920&height=2400&seq=database-mgmt-bg&orientation=portrait',
      icon: 'ri-hard-drive-line'
    },
    {
      id: 'data-migration',
      title: 'Data Migration Services',
      description: 'Seamlessly transfer your data across platforms with zero downtime. Our migration experts ensure complete data integrity.',
      features: ['Zero Downtime', 'Data Validation', 'Security Protocols', 'Rollback Protection'],
      bgImage: 'https://readdy.ai/api/search-image?query=dynamic%20data%20transfer%20visualization%20with%20flowing%20streams%20of%20information%20moving%20between%20different%20platforms%2C%20bridge-like%20connections%20spanning%20across%20digital%20landscapes%2C%20teal%20and%20orange%20color%20palette%2C%20motion%20blur%20effects%20suggesting%20movement&width=1920&height=2400&seq=data-migration-bg&orientation=portrait',
      icon: 'ri-exchange-line'
    },
    {
      id: 'ai-ml',
      title: 'AI and Machine Learning Solutions',
      description: 'Leverage cutting-edge AI and ML algorithms to automate processes and discover hidden patterns in your data.',
      features: ['Neural Networks', 'Deep Learning', 'Natural Language Processing', 'Computer Vision'],
      bgImage: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20brain%20network%20with%20neural%20pathways%20glowing%20in%20purple%20and%20pink%20colors%2C%20futuristic%20laboratory%20environment%2C%20floating%20AI%20models%20and%20algorithms%2C%20holographic%20projections%20of%20machine%20learning%20processes&width=1920&height=2400&seq=ai-ml-bg&orientation=portrait',
      icon: 'ri-brain-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=futuristic%20digital%20transformation%20landscape%20with%20flowing%20data%20streams%2C%20holographic%20interfaces%2C%20and%20advanced%20technology%20elements%2C%20dark%20blue%20and%20purple%20gradient%20background%2C%20cyberpunk%20aesthetic%20with%20neon%20accents&width=1920&height=1080&seq=hero-bg&orientation=landscape')`,
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 to-gray-950/90" />
        
        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-6xl lg:text-8xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent animate-pulse">
            Data-Driven Future
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            Transform your business with cutting-edge data analytics, AI, and intelligent solutions that drive innovation and growth
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-blue-500/25">
              Explore Solutions
            </button>
            <button className="px-8 py-4 border-2 border-gray-400 rounded-full font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Sections */}
      {sections.map((section, index) => (
        <section 
          key={section.id}
          className="relative min-h-screen flex items-center py-20"
          onMouseEnter={() => setHoveredSection(section.id)}
          onMouseLeave={() => setHoveredSection(null)}
        >
          {/* Background Image with Parallax */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('${section.bgImage}')`,
              transform: `translateY(${scrollY * (index % 2 === 0 ? 0.3 : -0.3)}px)`,
              filter: hoveredSection === section.id ? 'brightness(1.2)' : 'brightness(0.7)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-transparent" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'}`}>
              {/* Content */}
              <div className={`space-y-8 transform transition-all duration-700 ${hoveredSection === section.id ? 'translate-x-4 scale-105' : ''} ${index % 2 === 0 ? '' : 'lg:col-start-2'}`}>
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transform transition-all duration-500 ${hoveredSection === section.id ? 'rotate-12 scale-110' : ''}`}>
                    <i className={`${section.icon} text-2xl text-white`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                    {section.title}
                  </h2>
                </div>
                
                <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                  {section.description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {section.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className={`p-4 bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-blue-500/50 transform transition-all duration-300 hover:scale-105 hover:bg-gray-800/80 cursor-pointer ${hoveredSection === section.id ? 'animate-pulse' : ''}`}
                      style={{
                        animationDelay: `${featureIndex * 0.1}s`
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <i className="ri-check-line text-green-400 text-lg"></i>
                        <span className="text-gray-200 font-medium">{feature}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-4 pt-4">
                  <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-xl hover:shadow-blue-500/25">
                    Learn More
                  </button>
                  <button className="px-6 py-3 border-2 border-gray-600 rounded-full font-semibold hover:bg-gray-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer">
                    Get Demo
                  </button>
                </div>
              </div>
              
              {/* 3D Model Placeholder */}
              <div className={`relative h-96 lg:h-[500px] ${index % 2 === 0 ? '' : 'lg:col-start-1'}`}>
                <div className={`w-full h-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-3xl backdrop-blur-sm border border-gray-700/50 flex items-center justify-center transform transition-all duration-700 hover:rotate-3 hover:scale-105 ${hoveredSection === section.id ? 'animate-bounce' : ''}`}>
                  <div className="text-center space-y-4">
                    <div className={`w-24 h-24 mx-auto bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center transform transition-all duration-500 ${hoveredSection === section.id ? 'rotate-45 scale-110' : ''}`}>
                      <i className={`${section.icon} text-4xl text-white`}></i>
                    </div>
                    <p className="text-gray-400 text-lg">Interactive 3D Model</p>
                    <div className="flex justify-center space-x-2">
                      {[...Array(3)].map((_, i) => (
                        <div 
                          key={i}
                          className={`w-3 h-3 bg-blue-500 rounded-full animate-pulse`}
                          style={{
                            animationDelay: `${i * 0.3}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="relative py-32">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=futuristic%20technology%20conference%20room%20with%20holographic%20displays%20and%20data%20visualizations%2C%20modern%20corporate%20environment%20with%20glass%20surfaces%20and%20ambient%20lighting%2C%20blue%20and%20purple%20color%20scheme&width=1920&height=1080&seq=cta-bg&orientation=landscape')`,
            transform: `translateY(${scrollY * 0.2}px)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-900/90 to-gray-950" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-6xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Ready to Transform?
          </h2>
          <p className="text-xl sm:text-2xl text-gray-300 mb-12 leading-relaxed">
            Join thousands of companies already leveraging our advanced data solutions
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <button className="px-12 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-bold text-lg hover:scale-105 transform transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-purple-500/25">
              Start Your Journey
            </button>
            <button className="px-12 py-4 border-2 border-gray-400 rounded-full font-bold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Contact Sales
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
